import { Box, IconButton, Stack } from "@mui/material";
import { useState, useEffect } from "react";
import ChevronLeftSharpIcon from "@mui/icons-material/ChevronLeftSharp";
import ChevronRightSharpIcon from "@mui/icons-material/ChevronRightSharp";
import { AWS_URL } from "../../../configs/constants";
import colorConfigs from "../../../configs/colorConfigs";
import { typo } from "../../../assets/fonts/font";

interface ProductCarouselProps {
  imageArray: string[];
}

const ProductCarousel = ({ imageArray }: ProductCarouselProps) => {
  const extendedImages = [
    imageArray[imageArray.length - 1],
    ...imageArray,
    imageArray[0],
  ];

  const [currentIndex, setCurrentIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextItem = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const prevItem = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        if (currentIndex >= extendedImages.length - 1) {
          setCurrentIndex(1);
        }
        if (currentIndex <= 0) {
          setCurrentIndex(imageArray.length);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, extendedImages.length, isTransitioning, imageArray.length]);

  const activeIndex = currentIndex - 1;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "100%",
          transition: isTransitioning ? "transform 0.5s ease-in-out" : "none",
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {extendedImages.map((image, index) => (
          <Box
            key={index}
            sx={{
              minWidth: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...typo.bodyContent,
            }}
          >
            <img
              src={`${AWS_URL}/${image}`}
              alt={`banner-${index}`}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                userSelect: "none",
              }}
            />
          </Box>
        ))}
      </Box>

      <IconButton
        onClick={prevItem}
        sx={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          color: "var(--color-black)", // Set the icon color
          backgroundColor: "var(--color-transparent)", // Remove the background color
          "&:hover": {
            transform: "translateY(-50%) scale(1.2)", // Increase size on hover
          },
        }}
      >
        <ChevronLeftSharpIcon sx={{ fontSize: "36px" }} />
      </IconButton>

      <IconButton
        onClick={nextItem}
        sx={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          color: "var(--color-black)", // Set the icon color
          backgroundColor: "var(--color-transparent)", // Remove the background color
          "&:hover": {
            transform: "translateY(-50%) scale(1.2)", // Increase size on hover
          },
        }}
      >
        <ChevronRightSharpIcon sx={{ fontSize: "36px" }} />
      </IconButton>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: "absolute",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2,
        }}
      >
        {imageArray.map((_, index) => (
          <Box
            key={index}
            onClick={() => {
              setIsTransitioning(true);
              setCurrentIndex(index + 1);
            }}
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              border: "1px solid var(--color-black)",
              bgcolor:
                activeIndex === index ||
                (activeIndex === -1 && index === imageArray.length - 1) ||
                (activeIndex === imageArray.length && index === 0)
                  ? colorConfigs.primary
                  : "var(--color-white)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.2)",
              },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default ProductCarousel;
